// ─── orthoBonsDeCommande.js ──────────────────────────────────────────
// Où cocher, sur le papier, chaque analyse Ortho prescrite dans le SaaS.
//
// POURQUOI CE FICHIER
//
// Anissa remplit à la main les bons de commande papier d'Ortho-Analytic,
// devant la cliente, puis les lui donne pour le laboratoire. Le bon principal
// est en ALLEMAND et elle ne lit pas l'allemand. Elle doit donc faire
// correspondre des chaînes de caractères à l'œil.
//
// Ce catalogue lui dit quoi cocher : le libellé EXACT tel qu'imprimé, sa
// traduction, la section, le prélèvement et le kit.
//
// ⚠ ON NE DÉSIGNE JAMAIS UNE POSITION SEULE. Les numéros de section ne
// veulent pas dire la même chose d'un bon à l'autre : « 2.3 » désigne
// l'intolérance à l'histamine sur le bon français et les vitamines sur
// l'allemand. Le libellé fait foi, le numéro ne sert qu'à guider l'œil.
//
// ⚠ CHAQUE BON ENVOYÉ EST FACTURÉ 21.60 CHF (Auftragspauschale). C'est le
// nombre de BONS qui compte, pas le nombre de prélèvements. Voir labFees.js :
// l'estimation par type de prélèvement se trompe dans les deux sens.
//
// Sources : bons scannés le 11.06.2026 (FR V4.0 du 04.03.2026, DE
// 23IntMed_DCH V7.0 du 02.06.2026) et Preisliste Integrative Medizin
// V12.0/04-2026.
//
// ⚠ À RELIRE PAR ANISSA, BON EN MAIN, avant tout usage. Une faute de
// transcription ici fait commander la mauvaise analyse.

/** Les deux bons papier, avec leur version. Un bon = un envoi = 21.60 CHF. */
export const BONS = Object.freeze({
  DE: Object.freeze({
    id: 'de',
    label: 'Bon allemand',
    version: '23IntMed_DCH V7.0 · 02.06.2026',
    langue: 'de',
    // Le consentement n'est pas à la même page d'un bon à l'autre. Une
    // signature oubliée fait refuser le prélèvement.
    consentement: 'Signature page 4',
  }),
  FR: Object.freeze({
    id: 'fr',
    label: 'Bon français',
    version: 'V4.0 · 04.03.2026',
    langue: 'fr',
    consentement: 'Signature en bas de la page 1',
  }),
});

/**
 * @typedef {Object} EmplacementBon
 * @property {string} bon       - clé de BONS
 * @property {string} section   - numéro de section imprimé sur ce bon
 * @property {string} libelle   - libellé EXACT tel qu'imprimé
 * @property {string} [aide]    - traduction, quand le libellé est en allemand
 */

/**
 * @typedef {Object} EmplacementTest
 * @property {string} code            - code du test dans `lab_tests`
 * @property {string} prelevement     - ce qu'il faut prélever, en clair
 * @property {string|null} kit        - code du kit imprimé sur le bon
 * @property {EmplacementBon[]} bons  - où cocher, un par bon possible
 */

/**
 * Où cocher chaque test. Le premier emplacement est celui à privilégier.
 *
 * Les libellés allemands sont recopiés caractère par caractère depuis le bon,
 * y compris les majuscules : c'est ce qu'Anissa compare à l'œil.
 *
 * @type {ReadonlyArray<EmplacementTest>}
 */
export const EMPLACEMENTS = Object.freeze([
  Object.freeze({
    code: 'ortho_mikroernaehrung',
    prelevement: 'Sang',
    kit: null,
    bons: Object.freeze([
      Object.freeze({
        bon: 'DE', section: '2.1', libelle: 'Mikroernährung',
        aide: 'Micronutrition : ferritine, profil acides gras, folates, holotranscobalamine, homocystéine, hsCRP, cuivre, sélénium, vitamine D, zinc',
      }),
    ]),
  }),
  Object.freeze({
    code: 'ortho_mikroernaehrung_plus',
    prelevement: 'Sang',
    kit: null,
    bons: Object.freeze([
      Object.freeze({
        bon: 'DE', section: '2.1', libelle: 'Mikroernährung Plus',
        aide: 'Micronutrition + coenzyme Q10, LDL oxydées, vitamine A',
      }),
    ]),
  }),
  Object.freeze({
    code: 'ortho_cortisol_day',
    prelevement: 'Salive',
    kit: 'TS 401',
    bons: Object.freeze([
      Object.freeze({
        bon: 'DE', section: '6', libelle: 'Cortisol Tagesprofil',
        aide: 'Cortisol au fil de la journée',
      }),
    ]),
  }),
  Object.freeze({
    code: 'ortho_cortisol_dhea',
    prelevement: 'Salive',
    kit: 'TS 401',
    bons: Object.freeze([
      Object.freeze({
        bon: 'DE', section: '6', libelle: 'Cortisol-DHEA-Tagesprofil',
        aide: 'Cortisol et DHEA au fil de la journée',
      }),
    ]),
  }),
  Object.freeze({
    code: 'ortho_haarmineral_39',
    prelevement: 'Cheveux, 4 cm maximum',
    kit: 'TS 101',
    bons: Object.freeze([
      Object.freeze({
        bon: 'DE', section: '3.4', libelle: 'Haarmineral-Analyse 39 Elemente',
        aide: 'Analyse minérale capillaire, 39 éléments',
      }),
      Object.freeze({
        bon: 'FR', section: '3', libelle: 'Analyse minérale capillaire 39 éléments',
      }),
    ]),
  }),
  Object.freeze({
    code: 'ortho_mikrobiom_advance',
    prelevement: 'Selles stabilisées',
    kit: 'TS 204',
    bons: Object.freeze([
      Object.freeze({
        bon: 'DE', section: '1.3', libelle: 'Mikrobiom ADVANCED',
        aide: 'Microbiome avancé',
      }),
      Object.freeze({
        bon: 'FR', section: '1.1', libelle: 'Analyse du microbiome AVANCÉ',
      }),
    ]),
  }),
  Object.freeze({
    code: 'ortho_mikrobiom_advance_plus',
    prelevement: 'Selles stabilisées',
    kit: 'TS 204',
    bons: Object.freeze([
      Object.freeze({
        bon: 'DE', section: '1.3', libelle: 'Mikrobiom ADVANCED Plus',
        aide: 'Microbiome avancé + alpha-1-antitrypsine, calprotectine, histamine, IgA sécrétoire, zonuline',
      }),
      Object.freeze({
        bon: 'FR', section: '1.1', libelle: 'Analyse du microbiome AVANCÉ Plus',
      }),
    ]),
  }),
  Object.freeze({
    code: 'ortho_mikrobiom_complete',
    prelevement: 'Selles stabilisées',
    kit: 'TS 204',
    bons: Object.freeze([
      Object.freeze({
        bon: 'DE', section: '1.3', libelle: 'Mikrobiom COMPLETE',
        aide: 'Microbiome avancé + profil parasitaire',
      }),
      Object.freeze({
        bon: 'FR', section: '1.1', libelle: 'Analyse du microbiome COMPLET',
      }),
    ]),
  }),
  Object.freeze({
    code: 'ortho_mikrobiom_complete_plus',
    prelevement: 'Selles stabilisées',
    kit: 'TS 204',
    bons: Object.freeze([
      Object.freeze({
        bon: 'DE', section: '1.3', libelle: 'Mikrobiom COMPLETE Plus',
        aide: 'Microbiome complet + alpha-1-antitrypsine, calprotectine, histamine, IgA sécrétoire, zonuline',
      }),
      Object.freeze({
        bon: 'FR', section: '1.1', libelle: 'Analyse du microbiome COMPLET Plus',
      }),
    ]),
  }),
]);

const PAR_CODE = new Map(EMPLACEMENTS.map((e) => [e.code, e]));

/** Où cocher ce test, ou null si le catalogue ne le connaît pas. */
export function emplacementPourCode(code) {
  return PAR_CODE.get(String(code || '')) || null;
}

/**
 * Répartit des tests Ortho sur le minimum de bons possible.
 *
 * Comme chaque bon envoyé coûte 21.60 CHF, on cherche à tout faire tenir sur
 * un seul. Le bon allemand porte aujourd'hui l'intégralité de l'offre : la
 * répartition tombe donc presque toujours sur lui seul. On ne le suppose pas
 * pour autant, on le calcule, pour que l'ajout d'un test propre au bon
 * français se voie immédiatement.
 *
 * @param {string[]} codes
 * @returns {{
 *   bons: Array<{bon: object, lignes: Array<object>}>,
 *   inconnus: string[],
 * }}
 */
export function repartirSurLesBons(codes = []) {
  const demandes = [...new Set((Array.isArray(codes) ? codes : []).map(String))];
  const inconnus = demandes.filter((code) => !PAR_CODE.has(code));
  const connus = demandes.map((code) => PAR_CODE.get(code)).filter(Boolean);
  if (connus.length === 0) return Object.freeze({ bons: Object.freeze([]), inconnus: Object.freeze(inconnus) });

  // Un bon qui porte TOUS les tests demandés suffit : c'est 21.60 au lieu de
  // 43.20. On le prend s'il existe.
  const idsDeBons = Object.keys(BONS);
  const suffisant = idsDeBons.find((id) => connus.every(
    (test) => test.bons.some((place) => place.bon === id),
  ));

  const choisis = suffisant
    ? [suffisant]
    // Sinon, chaque test part sur son bon préféré, le premier de sa liste.
    : [...new Set(connus.map((test) => test.bons[0].bon))];

  const bons = choisis.map((id) => Object.freeze({
    bon: BONS[id],
    lignes: Object.freeze(connus
      .filter((test) => test.bons.some((place) => place.bon === id))
      .map((test) => {
        const place = test.bons.find((p) => p.bon === id);
        return Object.freeze({
          code: test.code,
          section: place.section,
          libelle: place.libelle,
          aide: place.aide || null,
          prelevement: test.prelevement,
          kit: test.kit,
        });
      })),
  }));

  return Object.freeze({ bons: Object.freeze(bons), inconnus: Object.freeze(inconnus) });
}

/**
 * Combien de bons Anissa devra remplir, donc combien de fois 21.60 CHF.
 *
 * Remplace l'estimation historique par nombre de types de prélèvement, qui se
 * trompe dans les deux sens : sang + selles + cheveux tiennent tous les trois
 * sur le bon allemand (un seul bon), tandis que deux prises de sang dont une
 * n'existe que sur le bon français en demandent deux.
 *
 * Rend `null` dès qu'un test échappe au catalogue : on ne sait alors pas sur
 * quel bon il tombe, et deviner sous-facturerait Anissa. L'appelant retombe
 * dans ce cas sur l'ancienne estimation, qui ne sous-facture jamais.
 *
 * @param {string[]} codes
 * @returns {number|null}
 */
export function nombreDeBonsPour(codes = []) {
  const demandes = (Array.isArray(codes) ? codes : []).map(String);
  if (demandes.length === 0) return 0;
  const { bons, inconnus } = repartirSurLesBons(demandes);
  if (inconnus.length > 0) return null;
  return bons.length;
}
